// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import {
	getArrayDiff,
	getObjectKeys,
	NullPartial,
	omitKeys,
	pickKeys,
	subsetTypeHasKey,
	ValueOf,
} from "@hex-insights/core";
import {
	EnrollmentApplicationStudentCreateInput,
	EnrollmentApplicationStudentDetailQuery,
	EnrollmentApplicationStudentInEnrollmentApplicationCreateInput,
	EnrollmentApplicationStudentUpdateInput,
} from "../../../Utilities";
import { EnrollmentApplicationStudentSchoolFormConversion } from "../EnrollmentApplicationStudentSchool";
import * as EnrollmentApplicationStudentFormValues from "./form-values";

/**
 * Converts the given `formValues` for the create form to a GraphQL create input for the EnrollmentApplicationStudent model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInput<
	K extends keyof EnrollmentApplicationStudentFormValues.Create = keyof EnrollmentApplicationStudentFormValues.Create,
>(formValues: EnrollmentApplicationStudentFormValues.CreateSubset<K>): EnrollmentApplicationStudentCreateInput {
	const inputFormValues = omitKeys(formValues, ["enrollmentApplicationStudentSchools"]);
	const input: NullPartial<EnrollmentApplicationStudentCreateInput> = {
		...inputFormValues,
		enrollmentApplicationStudentSchools: subsetTypeHasKey(formValues, "enrollmentApplicationStudentSchools")
			? formValues.enrollmentApplicationStudentSchools.map((e) =>
					EnrollmentApplicationStudentSchoolFormConversion.toGQLCreateInEnrollmentApplicationStudentInput(e),
			  )
			: undefined,
	};
	return input as EnrollmentApplicationStudentCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the EnrollmentApplication to a GraphQL nested create input for the EnrollmentApplicationStudent model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInEnrollmentApplicationInput<
	K extends keyof EnrollmentApplicationStudentFormValues.CreateInEnrollmentApplication = keyof EnrollmentApplicationStudentFormValues.CreateInEnrollmentApplication,
>(
	formValues: EnrollmentApplicationStudentFormValues.CreateInEnrollmentApplicationSubset<K>,
): EnrollmentApplicationStudentInEnrollmentApplicationCreateInput {
	const inputFormValues = omitKeys(formValues, ["enrollmentApplicationStudentSchools"]);
	const input: NullPartial<EnrollmentApplicationStudentInEnrollmentApplicationCreateInput> = {
		...inputFormValues,
		enrollmentApplicationStudentSchools: subsetTypeHasKey(formValues, "enrollmentApplicationStudentSchools")
			? formValues.enrollmentApplicationStudentSchools.map((e) =>
					EnrollmentApplicationStudentSchoolFormConversion.toGQLCreateInEnrollmentApplicationStudentInput(e),
			  )
			: undefined,
	};
	return input as EnrollmentApplicationStudentInEnrollmentApplicationCreateInput;
}

/**
 * Converts the given `formValues` for the detail form to a GraphQL update input for the EnrollmentApplicationStudent model.
 *
 * @param formValues The form values to convert. Generally only the changed form values.
 * @param initialFormValues The initial values of the form.
 */
export function toGQLUpdateInput<
	K extends keyof EnrollmentApplicationStudentFormValues.Detail = keyof EnrollmentApplicationStudentFormValues.Detail,
>(
	formValues: Partial<EnrollmentApplicationStudentFormValues.DetailSubset<K>>,
	initialFormValues: EnrollmentApplicationStudentFormValues.DetailSubset<K>,
): EnrollmentApplicationStudentUpdateInput {
	const inputFormValues = omitKeys(formValues, ["name", "languageIDs", "nationalityIDs"]);
	const input: EnrollmentApplicationStudentUpdateInput = { ...inputFormValues };

	if (subsetTypeHasKey(formValues, "englishLanguageFluency") && formValues.englishLanguageFluency === null) {
		input.clearEnglishLanguageFluency = true;
	}

	if (subsetTypeHasKey(formValues, "currentClassYearID") && formValues.currentClassYearID === null) {
		input.currentClassYearID = null;
		input.clearCurrentClassYear = true;
	}

	if (subsetTypeHasKey(formValues, "studentID") && formValues.studentID === null) {
		input.studentID = null;
		input.clearStudent = true;
	}

	if (
		subsetTypeHasKey(formValues, "languageIDs") &&
		subsetTypeHasKey(initialFormValues, "languageIDs") &&
		formValues.languageIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.languageIDs, formValues.languageIDs);
		input.addLanguageIDs = added;
		input.removeLanguageIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "nationalityIDs") &&
		subsetTypeHasKey(initialFormValues, "nationalityIDs") &&
		formValues.nationalityIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.nationalityIDs, formValues.nationalityIDs);
		input.addNationalityIDs = added;
		input.removeNationalityIDs = removed;
	}

	return input;
}

export type EnrollmentApplicationStudentFormKeyMap = {
	imageKey: "imageKey";
	imageContentType: "imageContentType";
	name: "name";
	givenName: "givenName";
	middleName: "middleName";
	familyName: "familyName";
	nameOrder: "nameOrder";
	dateOfBirth: "dateOfBirth";
	gender: "gender";
	englishLanguageFluency: "englishLanguageFluency";
	questionnaireActivities: "questionnaireActivities";
	questionnaireHasPsychTreatment: "questionnaireHasPsychTreatment";
	questionnairePsychTreatmentDescription: "questionnairePsychTreatmentDescription";
	questionnaireHasSpecialNeeds: "questionnaireHasSpecialNeeds";
	questionnaireSpecialNeedsDescription: "questionnaireSpecialNeedsDescription";
	questionnaireHasBehaviors: "questionnaireHasBehaviors";
	questionnaireBehaviorsDescription: "questionnaireBehaviorsDescription";
	questionnaireDescription: "questionnaireDescription";
	questionnaireAdditionalInformation: "questionnaireAdditionalInformation";
	academicYearID: "academicYear";
	classYearID: "classYear";
	currentClassYearID: "currentClassYear";
	enrollmentApplicationID: "enrollmentApplication";
	languageIDs: "languages";
	nationalityIDs: "nationalities";
	studentID: "student";
};

export type ToFormValuesArgs<
	K extends keyof EnrollmentApplicationStudentFormValues.Detail = keyof EnrollmentApplicationStudentFormValues.Detail,
> = {
	enrollmentApplicationStudent: Pick<
		EnrollmentApplicationStudentDetailQuery["enrollmentApplicationStudent"],
		ValueOf<Pick<EnrollmentApplicationStudentFormKeyMap, K>>
	>;
	fields?: { [Key in K]: true };
};

/**
 * Converts a EnrollmentApplicationStudent instance to its corresponding detail form values.
 *
 * @param enrollmentApplicationStudent The instance to convert.
 * @param fields The subset of fields to include.
 */
export function toFormValues<
	K extends keyof EnrollmentApplicationStudentFormValues.Detail = keyof EnrollmentApplicationStudentFormValues.Detail,
>({
	enrollmentApplicationStudent,
	fields,
}: ToFormValuesArgs<K>): EnrollmentApplicationStudentFormValues.DetailSubset<K> {
	const formValues = {
		imageKey: subsetTypeHasKey(enrollmentApplicationStudent, "imageKey") ? enrollmentApplicationStudent.imageKey : "",
		imageContentType: subsetTypeHasKey(enrollmentApplicationStudent, "imageContentType")
			? enrollmentApplicationStudent.imageContentType
			: "",
		name: subsetTypeHasKey(enrollmentApplicationStudent, "name") ? enrollmentApplicationStudent.name : "",
		givenName: subsetTypeHasKey(enrollmentApplicationStudent, "givenName")
			? enrollmentApplicationStudent.givenName
			: "",
		middleName: subsetTypeHasKey(enrollmentApplicationStudent, "middleName")
			? enrollmentApplicationStudent.middleName
			: "",
		familyName: subsetTypeHasKey(enrollmentApplicationStudent, "familyName")
			? enrollmentApplicationStudent.familyName
			: "",
		nameOrder: subsetTypeHasKey(enrollmentApplicationStudent, "nameOrder")
			? enrollmentApplicationStudent.nameOrder
			: null,
		dateOfBirth: subsetTypeHasKey(enrollmentApplicationStudent, "dateOfBirth")
			? enrollmentApplicationStudent.dateOfBirth
			: null,
		gender: subsetTypeHasKey(enrollmentApplicationStudent, "gender") ? enrollmentApplicationStudent.gender : "",
		englishLanguageFluency: subsetTypeHasKey(enrollmentApplicationStudent, "englishLanguageFluency")
			? enrollmentApplicationStudent.englishLanguageFluency
			: null,
		questionnaireActivities: subsetTypeHasKey(enrollmentApplicationStudent, "questionnaireActivities")
			? enrollmentApplicationStudent.questionnaireActivities
			: "",
		questionnaireHasPsychTreatment: subsetTypeHasKey(enrollmentApplicationStudent, "questionnaireHasPsychTreatment")
			? enrollmentApplicationStudent.questionnaireHasPsychTreatment
			: false,
		questionnairePsychTreatmentDescription: subsetTypeHasKey(
			enrollmentApplicationStudent,
			"questionnairePsychTreatmentDescription",
		)
			? enrollmentApplicationStudent.questionnairePsychTreatmentDescription
			: "",
		questionnaireHasSpecialNeeds: subsetTypeHasKey(enrollmentApplicationStudent, "questionnaireHasSpecialNeeds")
			? enrollmentApplicationStudent.questionnaireHasSpecialNeeds
			: false,
		questionnaireSpecialNeedsDescription: subsetTypeHasKey(
			enrollmentApplicationStudent,
			"questionnaireSpecialNeedsDescription",
		)
			? enrollmentApplicationStudent.questionnaireSpecialNeedsDescription
			: "",
		questionnaireHasBehaviors: subsetTypeHasKey(enrollmentApplicationStudent, "questionnaireHasBehaviors")
			? enrollmentApplicationStudent.questionnaireHasBehaviors
			: false,
		questionnaireBehaviorsDescription: subsetTypeHasKey(
			enrollmentApplicationStudent,
			"questionnaireBehaviorsDescription",
		)
			? enrollmentApplicationStudent.questionnaireBehaviorsDescription
			: "",
		questionnaireDescription: subsetTypeHasKey(enrollmentApplicationStudent, "questionnaireDescription")
			? enrollmentApplicationStudent.questionnaireDescription
			: "",
		questionnaireAdditionalInformation: subsetTypeHasKey(
			enrollmentApplicationStudent,
			"questionnaireAdditionalInformation",
		)
			? enrollmentApplicationStudent.questionnaireAdditionalInformation
			: "",
		academicYearID: subsetTypeHasKey(enrollmentApplicationStudent, "academicYear")
			? enrollmentApplicationStudent.academicYear?.id ?? null
			: null,
		classYearID: subsetTypeHasKey(enrollmentApplicationStudent, "classYear")
			? enrollmentApplicationStudent.classYear?.id ?? null
			: null,
		currentClassYearID: subsetTypeHasKey(enrollmentApplicationStudent, "currentClassYear")
			? enrollmentApplicationStudent.currentClassYear?.id ?? null
			: null,
		enrollmentApplicationID: subsetTypeHasKey(enrollmentApplicationStudent, "enrollmentApplication")
			? enrollmentApplicationStudent.enrollmentApplication?.id ?? null
			: null,
		languageIDs: subsetTypeHasKey(enrollmentApplicationStudent, "languages")
			? enrollmentApplicationStudent.languages.map((e) => e.id)
			: [],
		nationalityIDs: subsetTypeHasKey(enrollmentApplicationStudent, "nationalities")
			? enrollmentApplicationStudent.nationalities.map((e) => e.id)
			: [],
		studentID: subsetTypeHasKey(enrollmentApplicationStudent, "student")
			? enrollmentApplicationStudent.student?.id ?? null
			: null,
	};
	return fields ? pickKeys(formValues, getObjectKeys(fields)) : formValues;
}
